import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Users, GraduationCap, MessageSquare, Mail, Shield, Activity, Download, Upload, Loader2, HardDriveDownload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);

  const cards = [
    { icon: Users, label: "Użytkownicy", key: "users", color: "text-ztk-blue" },
    { icon: Shield, label: "Członkowie", key: "members", color: "text-emerald-500" },
    { icon: GraduationCap, label: "Szkolenia", key: "trainings", color: "text-ztk-yellow" },
    { icon: MessageSquare, label: "Wątki forum", key: "topics", color: "text-ztk-glow" },
    { icon: Activity, label: "Odpowiedzi", key: "replies", color: "text-purple-500" },
    { icon: Mail, label: "Wiadomości", key: "messages", color: "text-ztk-red" },
  ];

  const backup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : "ztkig-backup.zip";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Kopia zapasowa pobrana");
    } catch {
      toast.error("Błąd tworzenia kopii zapasowej");
    } finally { setDownloading(false); }
  };

  const restore = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("UWAGA: przywrócenie kopii NADPISZE obecne dane (użytkownicy, forum, szkolenia, treści). Tej operacji nie można cofnąć. Kontynuować?")) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Przywrócono kopię. Za chwilę nastąpi przeładowanie i ponowne logowanie.");
      setTimeout(() => { window.location.href = "/login"; }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd przywracania — sprawdź plik ZIP");
    } finally { setRestoring(false); }
  };

  return (
    <div>
      <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Przegląd</h2>
      <p className="mt-1 text-sm text-muted-foreground">Statystyki serwisu Izby Gospodarczej</p>
      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <div key={c.key} data-testid={`stat-${c.key}`} className="rounded-2xl border border-border bg-card p-6">
            <c.icon className={`h-7 w-7 ${c.color}`} />
            <div className="mt-4 font-display text-3xl font-extrabold text-ztk-navy dark:text-white">
              {stats ? stats[c.key] : "—"}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="mt-10 max-w-2xl rounded-2xl border border-border bg-card p-6" data-testid="backup-card">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-ztk-navy dark:text-white">
          <HardDriveDownload className="h-5 w-5 text-ztk-blue" /> Kopia zapasowa bazy danych
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Pobierz pełny zrzut bazy (użytkownicy, forum, szkolenia, treści, ustawienia) jako plik ZIP. Zalecamy regularne kopie w bezpiecznym miejscu.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={backup} disabled={downloading} className="gap-1.5 bg-ztk-blue" data-testid="backup-download">
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Pobierz kopię zapasową
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={restoring} variant="outline" className="gap-1.5" data-testid="restore-open">
            {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Przywróć z kopii (ZIP)
          </Button>
          <input ref={fileRef} type="file" accept=".zip,application/zip" onChange={restore} className="hidden" data-testid="restore-input" />
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-ztk-red">
          <RotateCcw className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Przywracanie nadpisuje wszystkie dane i wyloguje Cię — użyj tylko pliku pobranego z tej strony.
        </p>
      </div>
    </div>
  );
}
